/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-useless-return */
/* eslint-disable no-console */

import moment from 'moment';
import { isEmpty } from 'lodash';
import { leadFlatFields, leadTableFields } from 'src/constants/fields';
import { parseV2ToV1 } from 'src/utils/fieldUtils';
import {
    mergeAdAttributionIntoLeadPayload,
    clearStoredAdAttribution,
} from 'src/utils/adAttribution';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { dispatch, store } from '../../store';
import { updateError } from '../user';
import { callApi } from '../../../utils/apiCall';
import { hasFeatureAccess } from '../../../utils/featurePermission';
import { errorHandler } from '../../../utils/errorHandler';
import { localStorageRemoveItem } from '../../../utils/storage-available';
import { getCurrentTreatmentIdV2 } from '../clincheck';
import { setLoader } from '../loader';
import {
    leadActions,
    startLoadingData,
    updateCompleteStatus,
    updateDashNewLeadsTable,
    archiveLead
} from './slice';

import { addTreatment, editTreatment, UpdateTreatmentV2 } from './treatmentThunks';
import { addAppointment, getAppointment, followUpSlice, unableToContact } from './appointmentThunks';
import { addReminder, updateAssignToUser } from './taskThunks';
import { leadTreatmentPayment } from './financialThunks';

// ── Utility functions ────────────────────────────────────────

export const getDepositPendingStatus = (status) => status === 'NO';

// Builds the query-string suffix that scopes a request to a custom treatment tab.
// Delegates all include/exclude/sibling resolution to the backend via ?custom_table_tab=<id>.
// The backend resolves the tab by ID + practice, calls _build_custom_tab_q(), and applies the
// correct Q filter — covering ad-hoc excludes, sibling-tab excludes, and attribute predicates.
// The isV2Flat parameter is kept for signature compatibility but is no longer needed:
// the backend handles both V1 and V2-flat endpoints with the same param.
export const buildCustomTabQuery = (practiceId, customTabId, { isV2Flat = false } = {}) => {
    if (!customTabId) return '';
    return `&custom_table_tab=${encodeURIComponent(customTabId)}`;
};

export const getCurrentLeadAppointmentId = (id) => {
    const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
    if (!isEmpty(currentTreatmentDataGlobal?.treatmentData?.appointments)) return currentTreatmentDataGlobal?.treatmentData?.appointments?.[0]?.id;
    return id;
};

// TIMING CONTRACT: must stay > useTaskChecklistColumns' 150 ms debounce
// (src/hooks/useTaskChecklistColumns.js), or NewLeadDataGrid's checklist-switch
// gate drops before the cell fetch starts → blank-cell jank returns.
// Cross-ref: NewLeadDataGrid.jsx clearing effect (LEAD_LOADING_SETTLE_MS).
const LEAD_LOADING_SETTLE_MS = 500;

const handleLoading = (type, state) => {
    if (type === 'ConsultantClinCheckAttend') {
        if (state === false) {
            setTimeout(() => {
                dispatch(leadActions.setLoadingForClincheckLead(state));
            }, LEAD_LOADING_SETTLE_MS);
            return;
        }
        dispatch(leadActions.setLoadingForClincheckLead(state));
    } else if (state === false) {
        setTimeout(() => {
            dispatch(leadActions.setLoadingForLead(state));
        }, LEAD_LOADING_SETTLE_MS);
    } else {
        dispatch(leadActions.setLoadingForLead(state));
    }
};

const getQuery = (data) => {
    let query = '';
    if (data?.isNoTaskTable) {
        query += '&reminders__not_is_completed=false';
    }
    return query;
};

// ── Follow-up table threshold helper ─────────────────────────
// Tables: New (0), Follow up 1 (1), Follow up 2 (2), Follow up 3+ (3+)
function getFollowUpTable(count) {
    if (count <= 0) return 0;
    if (count === 1) return 1;
    if (count === 2) return 2;
    return 3;
}
const FOLLOW_UP_TABLE_LABELS = ['New', 'Follow up 1', 'Follow up 2', 'Follow up 3+'];

function checkFollowUpTableMove(leadState, treatmentId) {
    const lead = leadState?.followLead || leadState?.tableRowLead;
    if (!lead) return null;

    const treatments = lead?.lead_treatments || [];
    const treatment = treatmentId
        ? treatments.find((t) => t.id === treatmentId)
        : treatments[0];
    if (!treatment) return null;

    // Only counts for matching status (NEW) are relevant
    const counts = treatment?.lead_contacted_counts || [];
    const countObj = counts.find((c) => c.status === (treatment?.status || 'NEW')) || counts[0];
    const oldCount = countObj?.count ?? 0;
    const newCount = oldCount + 1;

    const oldTable = getFollowUpTable(oldCount);
    const newTable = getFollowUpTable(newCount);

    if (newTable !== oldTable) {
        const firstName = lead?.first_name || '';
        const lastName = lead?.last_name || '';
        return {
            leadId: lead?.id || lead?.lead_id,
            leadName: `${firstName} ${lastName}`.trim() || 'Lead',
            oldTable,
            newTable,
            oldLabel: FOLLOW_UP_TABLE_LABELS[oldTable],
            newLabel: FOLLOW_UP_TABLE_LABELS[newTable],
            treatmentName: treatment?.treatment?.name || treatment?.treatment_name || '',
        };
    }
    return null;
}

// ── Table data fetching ──────────────────────────────────────

export const getNewLead = ({ id, limit: rawLimit, offset: rawOffset, filterStatus, order, orderId, searchQuery, treatment, startDate, endDate, contactedCount, tags, referral, type, category, bookedWith, multiStatus, depositPendingStatus, apptType, consultationBookedWith, isNoTaskTable, currentTab, isArchived, isCacheApi, nhsProviderStatus, assigneeIds, forceV1API, isAnalyticsEnabled, noTreatment, acrossPractice, practices, leadIdsToSearch, bookedWidgetFilter, directCountFilter, customTabId, checklistState, forceRefetch }) =>
    async () => {
        // Strip undefined pagination params to prevent "limit=undefined" in URL
        const limit = rawLimit != null && !Number.isNaN(Number(rawLimit)) ? rawLimit : undefined;
        const offset = rawOffset != null && !Number.isNaN(Number(rawOffset)) ? rawOffset : undefined;
        if (limit === undefined || offset === undefined) {
            // eslint-disable-next-line no-console
            console.warn('[getNewLead] skipped: limit/offset undefined', { practiceId: id, type, limit, offset });
            return;
        }
        if (!isAnalyticsEnabled) {
            try {
                handleLoading(type, true);
                let healthCareProviderQuery = '';
                let healthCareProvider = isArchived ? '' : "&healthcare_provider=PRIVATE";
                if (nhsProviderStatus) {
                    healthCareProvider = "&healthcare_provider=NHS";
                    healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
                }
                const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;

                // Determine effective date and extra query based on bookedWidgetFilter
                const effectiveFilterStatus = filterStatus;
                let widgetDateOverride = '';
                let widgetExtraQuery = '';
                let skipWidgetDateFilter = false;
                if (bookedWidgetFilter) {
                    const today = moment().format('YYYY-MM-DD');
                    const nextWeek = moment().add(7, 'days').format('YYYY-MM-DD');
                    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
                    switch (bookedWidgetFilter) {
                        case 'upcoming':
                            widgetDateOverride = `&lead_treatments__appointments__datetime__range=${today} 00:00:00,${nextWeek} 23:59:00`;
                            widgetExtraQuery = '&lead_treatments__appointments__status=BOOKED';
                            break;
                        case 'awaiting':
                            // Skip date filter entirely - show all uncontacted BOOKED leads
                            skipWidgetDateFilter = true;
                            widgetExtraQuery = '&lead_treatments__lead_contacted_counts__isnull=true&lead_treatments__appointments__status=BOOKED';
                            break;
                        case 'past':
                            widgetDateOverride = `&lead_treatments__appointments__datetime__lte=${yesterday} 23:59:00`;
                            widgetExtraQuery = '&lead_treatments__appointments__status=BOOKED';
                            break;
                        default:
                            break;
                    }
                }

                const statusValue = effectiveFilterStatus === 'FOLLOW_UP' ? 'NEW' : effectiveFilterStatus;
                const queryParams = (!effectiveFilterStatus || effectiveFilterStatus === 'All' || (effectiveFilterStatus === 'NEW' && contactedCount === '0') || isAnalyticsEnabled) ? `limit=${limit}&offset=${offset}` : `lead_treatments__status=${statusValue}&limit=${limit}&offset=${offset}`;
                const customStartDate = `${startDate} 00:00:00`;
                const customEndDate = `${endDate} 23:59:00`;
                // Skip user date filter if widget has date override or skipWidgetDateFilter is set
                const date = skipWidgetDateFilter ? '' : (widgetDateOverride || ((startDate && endDate) ?
                    ((effectiveFilterStatus === 'CONSULTATION_BOOKED' || effectiveFilterStatus === 'CONSULTATION_ATTENDED' || effectiveFilterStatus === 'FAILED_TO_ATTEND_CONSULTATION') && !isAnalyticsEnabled
                        ? `&lead_treatments__appointments__datetime__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` :
                        (isAnalyticsEnabled && effectiveFilterStatus === 'All') ? `&state_transition_params={"timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}` :
                            (effectiveFilterStatus === 'TREATMENT_STARTED' || isAnalyticsEnabled ? `&state_transition_params={"state":"${effectiveFilterStatus}","timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}`
                                : `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`)) : ''));

                // Sanitize treatment: accept comma-string ("1,2") or array ([1,2]/{id}
                // objects); strip any non-numeric tokens so a stale name (e.g. "Veneers")
                // never reaches the backend as `lead_treatments__treatment__in=Veneers`.
                const _normTreatment = (() => {
                    if (!treatment) return null;
                    const tokens = Array.isArray(treatment)
                        ? treatment.map((v) => (v != null && typeof v === 'object' ? v.id : v))
                        : String(treatment).split(',');
                    const ids = tokens
                        .map((t) => (t != null ? String(t).trim() : ''))
                        .filter((t) => t !== '' && !Number.isNaN(Number(t)));
                    return ids.length > 0 ? ids.join(',') : null;
                })();
                const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : _normTreatment ? `&lead_treatments__treatment__in=${_normTreatment}` : '';

                // Task #15 latent-bug guard: contactedCount=undefined/null must not
                // produce contacted_count_for_status=undefined,gte,... in the query.
                // Any caller that omits contactedCount (e.g. AttendedChecklists which
                // uses checklistState exclusively) is safe even without the caller-side fix.
                const newLead = ((type === 'NewLeadTable' || type === 'ConsultantAttend' || type === 'AttendedChecklists' || type === 'ConsultantClinCheckAttend' || type === 'DashNewLeadTable' || (type === 'Dashboard' && (filterStatus === 'NEW' || filterStatus === 'FOLLOW_UP'))) && contactedCount != null && contactedCount !== '100000' && contactedCount !== 'All') ? (contactedCount <= 2 ? filterStatus === 'FOLLOW_UP' ? `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}`) : null;
                let newLeadParam = newLead;
                if (newLead && (filterStatus === 'CONSULTATION_ATTENDED' || type === 'AttendedChecklists')) {
                    const effectiveStatus = effectiveFilterStatus === 'FOLLOW_UP' ? 'NEW' : effectiveFilterStatus;
                    if (contactedCount <= 2) {
                        newLeadParam = filterStatus === 'FOLLOW_UP'
                            ? `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`
                            : `&contacted_count_for_status=${contactedCount},exact,${effectiveStatus}`;
                    } else {
                        newLeadParam = `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`;
                    }
                }
                const BlankCountnewLead = (nhsProviderStatus === 'EXISTING' || filterStatus === 'TREATMENT_STARTED' || filterStatus === 'CLOSED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION' || contactedCount === '100000') ? '' : filterStatus === 'NEW' ? '&has_new_or_null_treatment=true' : '&lead_treatments__lead_contacted_counts__isnull=true';
                const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
                const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';

                const bookedWithFilter = (bookedWith && bookedWith.length) ? (filterStatus === 'TREATMENT_STARTED' ? `&appointments__assignees__user__id__in=${bookedWith}&appointments__type__in=TREATMENT` : `&lead_treatments__appointments__assignees__user__id__in=${bookedWith}`) : '';

                const consultationBookedWithFilter = (consultationBookedWith && consultationBookedWith.length) ? (filterStatus === 'TREATMENT_STARTED' ? `&appointments__assignees__user__id__in=${consultationBookedWith}&appointments__type__in=CONSULTATION` : `&lead_treatments__appointments__assignees__user__id__in=${consultationBookedWith}`) : '';

                const clinCheckStatusQuery = contactedCount === '100000' ? '&lead_treatments__leadtreatment_clincheck__isnull=false' : '';
                const useBlankCount = (contactedCount === '0' || contactedCount === 0) && filterStatus !== 'CONSULTATION_ATTENDED' && type !== 'AttendedChecklists';
                const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${useBlankCount ? BlankCountnewLead : (newLeadParam || '')}${filterTags}${leadReferral}${bookedWithFilter}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${useBlankCount ? BlankCountnewLead : (newLeadParam || '')}${filterTags}${leadReferral}${bookedWithFilter}`;
                const treatmentCategory = (category !== '0' && category) ? effectiveFilterStatus === 'TREATMENT_STARTED' ? `&treatment__treatment_item__treatment_category__in=${category}` : `&lead_treatments__treatment__treatment_item__treatment_category__in=${category}` : '';
                const consultationBookedMethod = (type === 'ConsultantBooked' && contactedCount !== 'All' && !(typeof contactedCount === 'string' && contactedCount.startsWith('custom:'))) ? `&lead_treatments__appointments__method__in=${contactedCount}` : '';
                const multiStatusQuery = multiStatus ? `&lead_treatments__status__in=${multiStatus}` : '';
                const depositPendingStatusQuery = depositPendingStatus ? `&lead_treatments__is_treatment_fee_pending=${getDepositPendingStatus(depositPendingStatus)}` : '';
                // const appointmentTypeQuery = apptType ? `lead_treatments__appointments__type__in=${apptType}` : '';
                const isDataArchived = isArchived ? `&is_archived=true` : '&is_archived=false';
                const currentTabQuery = currentTab === 'Completed' ? `&lead_treatments__lead_treatment_attributes__key__in=Approval Status&lead_treatment_attributes__contains=${JSON.stringify([{ value: 'Arrived' }])}` : currentTab === 'invisalignFit' ? '&has_null_lead_treatment_attributes_or_not_arrived=true' : currentTab === 'invisalignAdditional' ? '&lead_treatment_attributes__cycle__in=2,3,4,5,6,7' : '';
                const fetchAcrossPractices = acrossPractice === true;
                const practiceFilters = practices?.length ? `&practice__in=${practices}` : ``;

                // All Practices opt-in (phase-2 consumer): append
                // `&practice__in=...` from the global activePracticeIds
                // state so the user's multi-practice selection
                // aggregates across every page the selector reaches,
                // not just analytics. `practiceInSuffix` returns '' for
                // single-practice users — the /practices/{id}/ URL path
                // already scopes them correctly, no need to round-trip
                // the same id through a query param.
                const allPracticesSuffix = practiceInSuffix(
                    store.getState().practiceTreatmentList?.activePracticeIds,
                );

                const assigneeIdsFilter = effectiveFilterStatus === 'TREATMENT_STARTED' && !forceV1API ? (assigneeIds ? `&lead__assignees__user__id__in=${assigneeIds}` : '') : (assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '')

                let apiQuery = effectiveFilterStatus === 'TREATMENT_STARTED' && !forceV1API ? `/api/v1/practices/${id}/lead-treatments-flat/${search}${clinCheckStatusQuery}${treatmentCategory}${consultationBookedMethod}${multiStatusQuery}${depositPendingStatusQuery}${isDataArchived}&${leadFlatFields}&${consultationBookedWithFilter}${getQuery({ isNoTaskTable })}${currentTabQuery}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${widgetExtraQuery}${allPracticesSuffix}` :
                    `/api/v1/practices/${id}/leads/${search}${clinCheckStatusQuery}${treatmentCategory}${consultationBookedMethod}${multiStatusQuery}${depositPendingStatusQuery}${isDataArchived}&${leadTableFields}&${consultationBookedWithFilter}${getQuery({ isNoTaskTable })}${currentTabQuery}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${widgetExtraQuery}${allPracticesSuffix}`;
                if (effectiveFilterStatus === 'TREATMENT_STARTED' && !forceV1API) {
                    apiQuery = apiQuery.replaceAll("lead_treatments__", "");
                    apiQuery = apiQuery.replaceAll("tagged_items__tag__name__in", "lead__lead_tags__tag__tag__name__in");
                    apiQuery = apiQuery.replaceAll("lead_source__referral__in", "lead__lead_source__referral__in");
                }

                if ((apiQuery?.includes("lead_treatments__lead_contacted_counts__count") || apiQuery?.includes("lead_treatments__lead_contacted_counts__count__gte")) &&
                    !(filterStatus === 'All' || (filterStatus === 'NEW' && contactedCount === '0') || isAnalyticsEnabled || filterStatus === 'CONSULTATION_ATTENDED')) {
                    apiQuery = `${apiQuery}&lead_treatments__lead_contacted_counts__status__in=${filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus}`
                }

                if (fetchAcrossPractices) {
                    apiQuery = `/api/v1/leads-flat/${search}${clinCheckStatusQuery}${treatmentCategory}${consultationBookedMethod}${multiStatusQuery}${depositPendingStatusQuery}${isDataArchived}&${leadTableFields}&${consultationBookedWithFilter}${practiceFilters}${getQuery({ isNoTaskTable })}${currentTabQuery}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}`
                }
                if (fetchAcrossPractices && leadIdsToSearch) {
                    apiQuery = `/api/v1/leads-flat/?${practiceFilters}&id__in=${leadIdsToSearch?.join(",")}`
                }
                if (directCountFilter) {
                    apiQuery = `${apiQuery}${directCountFilter}`;
                }
                // checklist_state filter — for AttendedChecklists (standalone route),
                // ConsultantAttend and TreatmentStarted custom tabs with checklist_binding
                // active. FE strip value IS the BE value ('in_progress' | 'complete' | 'none');
                // no normalization needed (canonicalized 2026-05-28).
                // Type guard removed: checklistState is only non-empty when the active
                // sub-strip is a checklist strip, so passing through unconditionally is safe.
                if (checklistState) {
                    apiQuery = `${apiQuery}&checklist_state=${checklistState}`;
                }
                // Custom treatment-scoped sub-tab — additive layer on top of the parent's resolved query.
                // See docs/superpowers/specs/2026-05-01-custom-treatment-sub-tabs-design.md
                const isV2Flat = effectiveFilterStatus === 'TREATMENT_STARTED' && !forceV1API;
                apiQuery += buildCustomTabQuery(id, customTabId, { isV2Flat });
                if (forceRefetch) apiQuery = `${apiQuery}&_t=${Date.now()}`;
                const response = await callApi(apiQuery);
                dispatch(leadActions.setTreatmentLoading(false));

                if (response?.status === 200) {
                    let result = response.data;
                    if (filterStatus === 'TREATMENT_STARTED' && !forceV1API)
                        result = { ...result, results: result?.results?.map((v2Data, index) => parseV2ToV1(v2Data ?? {}, `${index}${category != null ? `-${category}` : ''}`)) }


                    if (type === 'DashNewLeadTable') {
                        dispatch(leadActions.updateDashNewLead(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'NewLeadTable') {
                        dispatch(leadActions.leadStatusNew(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'ConsultantAttend') {
                        dispatch(leadActions.leadStatusConsultantAttended(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'ConsultantClinCheckAttend') {
                        dispatch(leadActions.leadStatusConsultantClinCheckAttended(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'AttendedChecklists') {
                        dispatch(leadActions.leadStatusAttendedChecklists(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'ConsultantBooked') {
                        dispatch(leadActions.leadStatusConsultantBooked(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'treatmentStarted') {
                        dispatch(leadActions.leadStatusTreatmentStarted(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'FailedToAttend') {
                        dispatch(leadActions.leadStatusFailedToAttend(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'Closed') {
                        dispatch(leadActions.leadStatusClosed(result));
                        dispatch(startLoadingData(false));
                        return;
                    }

                    dispatch(leadActions.newLead(result));

                }
                else {
                    dispatch(leadActions.setLoadingForExport(false));
                    handleLoading(type, false);
                    dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
                    return
                }

            } catch (error) {
                dispatch(leadActions.setTreatmentLoading(false));
                dispatch(leadActions.setLoadingForExport(false));
                dispatch(leadActions.hasError(error.message));
            }
            finally {
                handleLoading(type, false);
            }
        } else {
            try {
                handleLoading(type, true);
                const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
                const customStartDate = `${startDate} 00:00:00`;
                const customEndDate = `${endDate} 23:59:00`;
                const status = filterStatus === "NEW" ? `&lead_treatments__status=${filterStatus}` : ``;
                const date = (startDate && endDate) ?
                    ((filterStatus === 'CONSULTATION_BOOKED' || filterStatus === 'CONSULTATION_ATTENDED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION') && !isAnalyticsEnabled
                        ? `&lead_treatments__appointments__datetime__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` :
                        (isAnalyticsEnabled && (filterStatus === 'All' || filterStatus === 'NEW')) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` :
                            (filterStatus === 'TREATMENT_STARTED' || isAnalyticsEnabled ? `&state_transition_params={"state":"${filterStatus}","timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}`
                                : `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`)) : '';

                const treatmentCategory = (category !== '0' && category) ? filterStatus === 'TREATMENT_STARTED' ? `treatment__treatment_item__treatment_category__in=${category}` : `lead_treatments__treatment__treatment_item__treatment_category__in=${category}` : '';
                const isDataArchived = isArchived ? `&is_archived=true&is_archived_included=true` : '&is_archived=false';

                let result;

                // result = await isCacheValid(cacheNewLead, cacheKey, cacheTimer, currentTime, isCacheApi);

                const dashSuffix = practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds);
                let apiQuery = filterStatus === 'TREATMENT_STARTED' && !forceV1API ? `/api/v1/practices/${id}/lead-treatments-flat/?limit=${limit}&offset=${offset}${orderingLead}&${treatmentCategory}${date}${isDataArchived}&${leadFlatFields}${dashSuffix}` :
                    `/api/v1/practices/${id}/leads/?limit=${limit}&offset=${offset}${status}${orderingLead}&${treatmentCategory}${date}${isDataArchived}&${leadTableFields}${dashSuffix}`;

                if (filterStatus === 'TREATMENT_STARTED' && !forceV1API) {
                    apiQuery = apiQuery.replaceAll("lead_treatments__", "");
                    apiQuery = apiQuery.replaceAll("tagged_items__tag__name__in", "lead__lead_tags__tag__tag__name__in");
                    apiQuery = apiQuery.replaceAll("lead_source__referral__in", "lead__lead_source__referral__in");
                }
                const response = await callApi(apiQuery);

                if (response?.status === 200) {
                    result = response.data;
                    if (filterStatus === 'TREATMENT_STARTED' && !forceV1API)
                        result = { ...result, results: result?.results?.map((v2Data, index) => parseV2ToV1(v2Data ?? {}, `${index}${category != null ? `-${category}` : ''}`)) }
                }
                else {
                    dispatch(leadActions.setLoadingForExport(false));
                    handleLoading(type, false);
                    dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
                    return
                }

                dispatch(leadActions.setTreatmentLoading(false));

                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.leadStatusNew(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'ConsultantAttend') {
                    dispatch(leadActions.leadStatusConsultantAttended(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'ConsultantClinCheckAttend') {
                    dispatch(leadActions.leadStatusConsultantClinCheckAttended(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'AttendedChecklists') {
                    dispatch(leadActions.leadStatusAttendedChecklists(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'ConsultantBooked') {
                    dispatch(leadActions.leadStatusConsultantBooked(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'treatmentStarted') {
                    dispatch(leadActions.leadStatusTreatmentStarted(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'FailedToAttend') {
                    dispatch(leadActions.leadStatusFailedToAttend(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'Closed') {
                    dispatch(leadActions.leadStatusClosed(result));
                    dispatch(startLoadingData(false));
                    return;
                }

                dispatch(leadActions.newLead(result));

            } catch (error) {
                dispatch(leadActions.setTreatmentLoading(false));
                dispatch(leadActions.setLoadingForExport(false));
                dispatch(leadActions.hasError(error.message));
            }
            finally {
                handleLoading(type, false);
            }
        }
    };

export const getBlankTreatmentLeads = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, type, nhsProviderStatus, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `limit=${limit}&offset=${offset}&lead_treatments__isnull=${true}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}`;

            let healthCareProviderQuery = '';
            let healthCareProvider = "";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}${healthCareProvider}${healthCareProviderQuery}&is_archived=false&${leadTableFields}${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);
            if (response?.status === 200) {
                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(response.data));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.leadStatusNew(response.data));
                    dispatch(startLoadingData(false));
                }
            } else {
                dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };

export const getNHSLeads = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, type, healthCareProvider, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `lead_treatments__status=NEW&limit=${limit}&offset=${offset}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const nhsLead = healthCareProvider ? `&healthcare_provider=${healthCareProvider}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}` : `?${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}`;

            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false&${leadTableFields}&lead_note__note__not_in=NHS_WAITING_LIST${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);

            if (response?.status === 200) {
                const result = response.data;
                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.leadStatusNew(result));
                    dispatch(startLoadingData(false));
                }

            } else {
                dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
            }

        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };

export const getNoTreatmentCounter = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, nhsProviderStatus, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `limit=${limit}&offset=${offset}&lead_treatments__isnull=${true}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}`;

            let healthCareProviderQuery = '';
            let healthCareProvider = "";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false${healthCareProvider}${healthCareProviderQuery}&fields={id}${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);
            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                dispatch(leadActions.updateCounter([...counter, { filterStatus: 'No Treatment', count: result?.count }]));

            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }

        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };

export const getNHSCounter = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, healthCareProvider, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `lead_treatments__status=NEW&limit=${limit}&offset=${offset}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const nhsLead = healthCareProvider ? `&healthcare_provider=${healthCareProvider}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}` : `?${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}`;
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false&fields={id}&lead_note__note__not_in=NHS_WAITING_LIST${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);

            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                dispatch(leadActions.updateCounter([...counter, { filterStatus: 'NHS', count: result?.count }]));

            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }

        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };

export function getAwaitingLead({ id, type, data, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, nhsProviderStatus, assigneeIds, noTreatment, customTabId }) {
    return async () => {
        try {

            let healthCareProviderQuery = '';
            let healthCareProvider = '';
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `limit=${limit}&offset=${offset}&latest_note__in=AWAITING_RESPONSE`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const filterData = '&lead_treatments__status=NEW';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}${filterData}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}${filterData}`;
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            // Custom-tab additive filter — Awaiting uses V1 leads endpoint (isV2Flat=false).
            const customTabQuery = buildCustomTabQuery(id, customTabId);

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false${healthCareProvider}${healthCareProviderQuery}&${leadTableFields}${assigneeIdsFilter}${customTabQuery}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);

            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                dispatch(leadActions.updateCounter([...counter, { key: 'AWAITING_RESPONSE', filterStatus: 'AWAITING_RESPONSE', count: result?.count }]));

                if (data === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(result));
                    return;
                }
                if (data === 'NewLeadTable' && type === 'AddData') {
                    dispatch(leadActions.leadStatusNew(result));
                }

            }
            else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }

        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };
}

export const getCounter = ({ id, filterStatus, order, orderId, searchQuery, treatment, startDate, endDate, contactedCount, tags, referral, type, bookedWith, depositPendingStatus, isNoTaskTable, multiStatus, category, isArchived, nhsProviderStatus, assigneeIds, forceV1API, noTreatment, getCounter, acrossPractice, practices, leadIdsToSearch, checklistState, forceRefetch, customTabId }) =>
    async () => {
        try {

            let healthCareProviderQuery = '';
            let healthCareProvider = isArchived ? '' : "&healthcare_provider=PRIVATE";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const counterStatusValue = filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus;
            const queryParams = (!filterStatus || filterStatus === 'All' || (filterStatus === 'NEW' && contactedCount === '0')) ? `limit=${1}&offset=${0}` : `lead_treatments__status=${counterStatusValue}&limit=${1}&offset=${0}`;
            const customStartDate = `${startDate} 00:00:00`;
            const customEndDate = `${endDate} 23:59:00`;
            const date = (startDate && endDate) ? (filterStatus === 'CONSULTATION_BOOKED' || filterStatus === 'CONSULTATION_ATTENDED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION' ? `&lead_treatments__appointments__datetime__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : (filterStatus === 'TREATMENT_STARTED' ? `&state_transition_params={"state":"${filterStatus}","timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}` : `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`)) : '';
            // Same sanitize guard as getNewLead — strips names/undefined/null tokens.
            const _normTreatmentC = (() => {
                if (!treatment) return null;
                const tokens = Array.isArray(treatment)
                    ? treatment.map((v) => (v != null && typeof v === 'object' ? v.id : v))
                    : String(treatment).split(',');
                const ids = tokens
                    .map((t) => (t != null ? String(t).trim() : ''))
                    .filter((t) => t !== '' && !Number.isNaN(Number(t)));
                return ids.length > 0 ? ids.join(',') : null;
            })();
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : _normTreatmentC ? `&lead_treatments__treatment__in=${_normTreatmentC}` : '';
            const newLead = ((type === 'NewLeadTable' || type === 'ConsultantAttend' || type === 'AttendedChecklists' || type === 'ConsultantClinCheckAttend' || type === 'DashNewLeadTable' || type === 'Dashboard') && contactedCount != null && contactedCount !== '100000' && contactedCount !== 'All') ? (contactedCount <= 2 ? filterStatus === 'FOLLOW_UP' ? `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}`) : null;
            let newLeadParam = newLead;
            if (newLead && (filterStatus === 'CONSULTATION_ATTENDED' || type === 'AttendedChecklists')) {
                const effectiveStatus = filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus;
                if (contactedCount <= 2) {
                    newLeadParam = filterStatus === 'FOLLOW_UP'
                        ? `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`
                        : `&contacted_count_for_status=${contactedCount},exact,${effectiveStatus}`;
                } else {
                    newLeadParam = `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`;
                }
            }
            const BlankCountnewLead = (filterStatus === 'TREATMENT_STARTED' || filterStatus === 'CLOSED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION' || contactedCount === '100000') ? '' : filterStatus === 'NEW' ? '&has_new_or_null_treatment=true' : '&lead_treatments__lead_contacted_counts__isnull=true';
            const filterTags = tags && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const bookedWithFilter = (bookedWith && bookedWith.length) ? (filterStatus === 'TREATMENT_STARTED' ? `&appointments__assigned_to__user__id__in=${bookedWith}&appointments__type__in=TREATMENT` : `&lead_treatments__appointments__assignees__user__id__in=${bookedWith}`) : '';

            const clinCheckStatusQuery = contactedCount === '100000' ? '&lead_treatments__leadtreatment_clincheck__isnull=false' : '';
            const depositPendingStatusQuery = depositPendingStatus ? `&lead_treatments__is_treatment_fee_pending=${getDepositPendingStatus(depositPendingStatus)}` : '';
            const multiStatusQuery = multiStatus ? `&lead_treatments__status__in=${multiStatus}` : '';
            const treatmentCategory = (category !== '0' && category) ? `&lead_treatments__treatment__treatment_item__treatment_category__in=${category}` : '';
            const useBlankCount = (contactedCount === '0' || contactedCount === 0) && filterStatus !== 'CONSULTATION_ATTENDED' && type !== 'AttendedChecklists';
            const search = (searchQuery && searchQuery !== null && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${useBlankCount ? BlankCountnewLead : (newLeadParam || '')}${filterTags}${leadReferral}${bookedWithFilter}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${useBlankCount ? BlankCountnewLead : (newLeadParam || '')}${filterTags}${leadReferral}${bookedWithFilter}${clinCheckStatusQuery}`;
            const isDataArchived = isArchived ? `&is_archived_included=true` : '&is_archived=false';
            const fetchAcrossPractices = acrossPractice === true;
            const assigneeIdsFilter = filterStatus === 'TREATMENT_STARTED' ? (assigneeIds ? `&lead__assignees__user__id__in=${assigneeIds}` : '') : (assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '')
            const practiceFilters = practices?.length ? `&practice__in=${practices}` : ``;
            // All Practices phase-2: mirror the suffix from
            // activePracticeIds onto the counter endpoint too so widget
            // badges match the multi-practice lead-table rows. Empty
            // string for single-practice selection — existing URL scope
            // already covers that case.
            const allPracticesSuffix = practiceInSuffix(
                store.getState().practiceTreatmentList?.activePracticeIds,
            );

            let apiQuery = filterStatus === 'TREATMENT_STARTED' && !forceV1API ? `/api/v1/practices/${id}/lead-treatments-flat/${search}${depositPendingStatusQuery}${getQuery({ isNoTaskTable })}&${multiStatusQuery}&fields={}${isDataArchived}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${allPracticesSuffix}`
                : `/api/v1/practices/${id}/leads/${search}${depositPendingStatusQuery}${getQuery({ isNoTaskTable })}&${multiStatusQuery}&fields={}${isDataArchived}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${allPracticesSuffix}`;
            if (filterStatus === 'TREATMENT_STARTED' && !forceV1API) {
                apiQuery = apiQuery.replaceAll("lead_treatments__", "");
                apiQuery = apiQuery.replaceAll("lead_source__referral__in", "lead__lead_source__referral__in");
                apiQuery = apiQuery.replaceAll("tagged_items__tag__name__in", "lead__lead_tags__tag__tag__name__in");
            }

            if ((apiQuery?.includes("lead_treatments__lead_contacted_counts__count") || apiQuery?.includes("lead_treatments__lead_contacted_counts__count__gte")) &&
                !(filterStatus === 'All' || (filterStatus === 'NEW' && contactedCount === '0') || filterStatus === 'CONSULTATION_ATTENDED')) {
                apiQuery = `${apiQuery}&lead_treatments__lead_contacted_counts__status__in=${filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus}`
            }

            if (fetchAcrossPractices) {
                apiQuery = `/api/v1/leads-flat/${search}${depositPendingStatusQuery}${getQuery({ isNoTaskTable })}&${multiStatusQuery}&fields={}${isDataArchived}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${practiceFilters}`
            }

            if (fetchAcrossPractices && leadIdsToSearch) {
                apiQuery = `/api/v1/leads-flat/?${practiceFilters}&id__in=${leadIdsToSearch?.join(",")}`
            }

            if (checklistState) {
                // FE strip value is the canonical BE value — no normalization (2026-05-28).
                apiQuery = `${apiQuery}&checklist_state=${checklistState}`;
            }

            // getCounter always uses the V1 leads endpoint (isV2Flat=false).
            apiQuery += buildCustomTabQuery(id, customTabId);

            if (forceRefetch) apiQuery = `${apiQuery}&_t=${Date.now()}`;
            const response = await callApi(apiQuery);

            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: contactedCount, count: result?.count }]));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.updateCounter([...counter, { key: contactedCount, filterStatus: contactedCount, count: result?.count }]));

                    return;
                }
                if (type === 'ConsultantAttend') {
                    const key = checklistState ?? contactedCount;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                if (type === 'ConsultantClinCheckAttend') {
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: contactedCount, count: result?.count }]));
                    return;
                }
                if (type === 'AttendedChecklists') {
                    const key = checklistState ?? contactedCount;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                if (type === 'treatmentStarted') {
                    // Treatment Started has no contacted-count strip — only the
                    // checklist sub-strip. Key the count by its checklist state so
                    // NewLead's tabCounter('in_progress'|'complete') resolves it.
                    const key = checklistState ?? filterStatus;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                dispatch(leadActions.updateCounter([...counter, { filterStatus, count: result?.count }]));

            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }


        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };

export function dashNewTableCounter(id) {
    return async () => {

        const response = await callApi(`/api/v1/practices/${id}/leads/?has_new_or_null_treatment=true&fields={}&is_archived=false&healthcare_provider=PRIVATE${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);
        try {
            if (response?.status === 200) {
                const { dashNewContact } = store.getState().lead;
                dispatch(updateDashNewLeadsTable({ ...dashNewContact, labelCounter: response.data.count }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

// ── Lead CRUD ────────────────────────────────────────────────

export function savePersonalDetails(data, practiceId, handleClose) {
    return async () => {
        try {
            const { lead, activeTab, newLeadDetail, sendReminderDuringLeadCreate } = store.getState().lead;
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/`, 'post', mergeAdAttributionIntoLeadPayload(data), false, { timeout: 30000 });
            if (response?.status === 201) {
                // Clear stashed ad attribution after successful lead create
                // so subsequent leads in the same session don't all carry
                // the same gclid / UTMs.
                clearStoredAdAttribution();
                if (sendReminderDuringLeadCreate) {
                    const addReminderObj = { ...newLeadDetail?.lead_reminder, lead: response?.data?.id }
                    dispatch(addReminder(addReminderObj, practiceId, '', 'createLead'));
                }
                dispatch(leadActions.saveConsentLead(response?.data));
                dispatch(leadActions.saveLead(response?.data));
                const results = [response?.data, ...lead?.results];
                const count = lead?.count + 1;
                dispatch(leadActions.updateLead({ results, count }));
                if (activeTab >= 1 && newLeadDetail?.lead_treatment) {
                    const treatmentDetail = {
                        ...newLeadDetail.lead_treatment, is_practice_notification_required: false
                    };
                    dispatch(addTreatment(treatmentDetail, practiceId, response?.data?.id, handleClose, { needToUpdate: treatmentDetail.teethAmount || treatmentDetail.teethAmount === 0, teeth: treatmentDetail.teethAmount || 0 }));
                } else {
                    // If no treatment to add, call handleClose directly to close the dialog
                    handleClose?.('finish', response?.data?.id);
                }
                // handleNext?.({ success: true, message: 'Personal Details added Successfully' });
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));
                // handleNext?.({ success: false, message: response.data || 'Something went wrong' });
                // dispatch(leadActions.hasError(response.data || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(startLoadingData(false));
        }
    };
}

export function updatePersonalDetail(data, practiceId, handleClose, type) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data.id}/`, 'patch', data);
            if (response?.status === 200) {
                const { activeTab, newLeadDetail, attendance, tableRowLead, newLeadDetail: { archiveLead } } = store.getState().lead;
                if (type === 'leadDetail') {
                    dispatch(leadActions.saveLeadDetail(response.data));
                    dispatch(followUpSlice(data.id, practiceId, 'LeadDetail'));
                    dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                    handleClose('finish', data.id);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'personalDetail') {
                    dispatch(leadActions.saveLead(response.data));
                }
                if (attendance === 'payment' && activeTab > 2 && newLeadDetail.payment.payment_type !== 'No') {
                    dispatch(leadTreatmentPayment(practiceId, tableRowLead.lead_id, tableRowLead.treatment_id, newLeadDetail.payment, handleClose));
                    return;
                }
                if (type === 'archived') {
                    dispatch(archivedLeadAPI(archiveLead.archiveData, practiceId, handleClose, data.id));
                    return;
                }
                if (type === 'leadDataGrid') {
                    dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                    return;
                }
                if (type === 'leadTableEdit') {
                    handleClose({ success: true, message: 'Personal details updated successfully', data: response.data });
                    return;
                }
                if (type === 'EditPersonalDetail') {
                    dispatch(leadActions.saveLead(response.data));
                    dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                    handleClose();
                    dispatch(followUpSlice(data.id, practiceId));
                    dispatch(getAppointment(practiceId, data.id));
                    return;
                }
                dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                dispatch(updateCompleteStatus(true));
                if (type === 'leadDetail') {
                    handleClose(true);
                    return;
                }
                handleClose('finish', data.id);
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
            dispatch(startLoadingData(false));
        }
    };
}

export function patchLeadMetadata(practiceId, leadId, metadataArray, onSuccess) {
    return async () => {
        try {
            // Fetch existing metadata so we send the full array (backend replaces entirely)
            const leadRes = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`);
            const existing = Array.isArray(leadRes?.data?.lead_metadata) ? leadRes.data.lead_metadata : [];

            // Merge: update existing entries by key (preserve id), append new ones
            const merged = [...existing];
            metadataArray.forEach(entry => {
                const idx = merged.findIndex(m => m.key === entry.key);
                if (idx !== -1) {
                    merged[idx] = { ...merged[idx], value: entry.value };
                } else {
                    merged.push(entry);
                }
            });

            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                'patch',
                { lead_metadata: merged }
            );
            if (response?.status === 200) {
                if (typeof onSuccess === 'function') onSuccess(response.data);
            }
        } catch (error) {
            dispatch(updateError({ message: 'Failed to update lead metadata', success: false }));
        }
    };
}

export function createNewLead(handleClose) {
    return async () => {
        try {
            const { newLeadDetail } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;

            // Validate that personalDetail exists and has required fields before making API call
            const personalDetail = newLeadDetail?.personalDetail;

            if (!personalDetail || (!personalDetail.first_name && !personalDetail.lead_contact?.phone)) {
                dispatch(updateError({ message: 'Cannot create lead: Please fill in the required fields (First name and Phone number)', success: false }));
                dispatch(startLoadingData(false));
                dispatch(updateCompleteStatus(false));
                return;
            }

            dispatch(startLoadingData(true));
            dispatch(savePersonalDetails(personalDetail, practiceId, handleClose));
        } catch (error) {
            dispatch(updateError({ message: error.message, success: false }));
            dispatch(startLoadingData(false));
        }
    };
}

export const getCurrentTreatmentData = (data) => {
    const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
    if (!isEmpty(currentTreatmentDataGlobal?.treatmentData)) return currentTreatmentDataGlobal.treatmentData;
    return data;
};

export function updateLeadDetail(data, handleClose, isNoNotes, isRecursiveCall = false) {
    return async () => {
        try {
            const { newLeadDetail: { lead_appointment: leadAppoiment, archiveLead, lead_treatment: backupLeadTreatment }, followLead, newLeadDetail, activeTab } = store.getState().lead;
            const { lead_treatments: leadTreatment } = followLead ?? {};
            const { practiceId } = store.getState().practiceTreatmentList;
            if (!leadTreatment?.length) {
                dispatch(startLoadingData(false));
                dispatch(updateError({ message: 'Lead data not loaded yet. Please try again.', success: 'warning' }));
                return;
            }
            dispatch(startLoadingData(true));
            dispatch(setLoader(true));
            localStorageRemoveItem('localTreatmentData');
            const treatment = getCurrentTreatmentData(leadTreatment[0]);
            const { leadAssignees } = archiveLead ?? {};
            if (Array.isArray(leadAssignees) && leadAssignees.length) {
                try {
                    const ids = leadAssignees.filter(Boolean);
                    const targetLeadId = (treatment && treatment.lead) || followLead?.id;
                    if (targetLeadId && ids.length > 0) {
                        dispatch(updateAssignToUser({ assignees: ids }, targetLeadId, practiceId, () => { }));
                    }
                } catch (e) { /* noop */ }
            }
            if (treatment) {
                if (data === 'Unable to contact') {
                    dispatch(leadActions.saveTreatment(treatment));
                    dispatch(unableToContact(handleClose, isNoNotes));
                    return;
                }
                if (activeTab >= 1 && archiveLead && archiveLead.notes) {
                    const archiveNote = {
                        ...archiveLead.notes,
                        lead: treatment?.lead || followLead?.id,
                        treatment: treatment?.id
                    };

                    if (archiveNote.note === "NHS_WAITING_LIST") {
                        dispatch(addNotes(archiveNote, practiceId, handleClose, 'nhs'));
                        return;
                    }

                    dispatch(addNotes(archiveNote, practiceId, handleClose, 'archive'));
                    return;
                }
                const updatedTreatment = newLeadDetail && newLeadDetail.lead_treatment && newLeadDetail.lead_treatment || {};
                dispatch(leadActions.saveTreatment(treatment));
                // eslint-disable-next-line eqeqeq
                if (!isRecursiveCall && (treatment?.treatment !== updatedTreatment.treatment || treatment?.price != updatedTreatment?.custom_price || treatment?.leadtreatment_specification?.teeth_count != updatedTreatment.teethAmount)) {
                    const treatmentDetail = {
                        ...newLeadDetail.lead_treatment,
                        lead: treatment?.lead || followLead?.id,
                        id: treatment?.id
                    };
                    if (treatmentDetail?.id)
                        dispatch(UpdateTreatmentV2(treatmentDetail, practiceId, handleClose, 'updateLeadDetail'));
                    return;
                }
                dispatch(addAppointment({ ...leadAppoiment, lead_treatment: treatment?.id, lead: treatment?.lead || followLead?.id, }, practiceId, treatment?.lead || followLead?.id, handleClose, 'followUp'));
            } else if (!isRecursiveCall) {
                dispatch(addTreatment({ ...backupLeadTreatment, is_practice_notification_required: false }, practiceId, followLead?.id, () => dispatch(updateLeadDetail(data, handleClose, isNoNotes, true)), { needToUpdate: backupLeadTreatment?.teethAmount || backupLeadTreatment?.teethAmount === 0, teeth: backupLeadTreatment?.teethAmount || 0 }, true));
            } else {
                dispatch(updateError({ message: "No Treatment found for lead", success: "warning" }));
                dispatch(setLoader(false));
                dispatch(startLoadingData(false));
                if (activeTab >= 1 && archiveLead && archiveLead.notes) {
                    const archiveNote = {
                        ...archiveLead.notes,
                        lead: followLead?.id
                    };

                    if (archiveNote.note === "NHS_WAITING_LIST") {
                        dispatch(addNotes(archiveNote, practiceId, handleClose, 'nhs'));
                        return;
                    }

                    dispatch(addNotes(archiveNote, practiceId, handleClose, 'archive'));
                    return;
                }
            }
        } catch (error) {
            dispatch(setLoader(false));
            dispatch(updateError({ message: error.message, success: false }));
            dispatch(startLoadingData(false));
        }
    };
}

export function addNewLead(data, practiceId, treatmentDetail, formData, handleSuccess, type) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/`, 'post', mergeAdAttributionIntoLeadPayload(data), false, { timeout: 30000 });
            if (response?.status === 201) {
                // Clear stashed ad attribution on every successful lead
                // create so subsequent leads don't inherit the same gclid.
                clearStoredAdAttribution();
                dispatch(updateError({ success: true, message: 'Lead created successfully' }));
                dispatch(addTreatment(treatmentDetail, practiceId, response?.data?.id, handleSuccess, { needToUpdate: treatmentDetail?.teethAmount || treatmentDetail?.teethAmount === 0, teeth: treatmentDetail?.teethAmount || 0 }));
                handleSuccess({ success: true, leadId: response?.data?.id, formData, event: type });
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(startLoadingData(false));
        }
    };
}

// Quick lead creation for call scripts - creates a minimal lead and returns it via callback
export function quickCreateLead(practiceId, leadData, onSuccess, onError) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/`, 'post', mergeAdAttributionIntoLeadPayload(leadData));
            if (response?.status === 201) {
                // See savePersonalDetails — clear on success.
                clearStoredAdAttribution();
                dispatch(updateError({ success: true, message: 'Lead created successfully' }));
                onSuccess?.(response.data);
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                onError?.(response.data);
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            onError?.(error);
        }
    };
}

// ── Notes ────────────────────────────────────────────────────

export function addNotes(data, practiceId, handleClose, status, followLead) {
    return async () => {
        try {
            // Snapshot the current count BEFORE the API call so we can detect table moves
            const leadState = store.getState().lead;
            const tableMove = data?.contacted_via
                ? checkFollowUpTableMove(leadState, data?.treatment)
                : null;

            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/notes/`, 'post', data);

            if (response?.status === 201) {
                // Note: we intentionally do NOT dispatch setFollowUpTableMoved here.
                // The iOS toast it triggers is redundant — the user already knows they added a note.

                const { newLeadDetail, createLead, tableRowLead, newLeadDetail: { archiveLead } } = store.getState().lead;

                if (status === "createLead") return

                if (status === 'UNABLE_TO_CONTACT') {
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    handleClose();
                    return;
                }
                if (status === 'unable') {
                    if (newLeadDetail.addReminder) {
                        const reminderDetail = {
                            ...newLeadDetail.addReminder,
                            lead_treatment: getCurrentTreatmentIdV2(createLead.lead_treatment ? createLead.lead_treatment.id : 0),
                        };
                        dispatch(addReminder(reminderDetail, practiceId, handleClose, 'unable'));
                        return;
                    }
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'attended') {
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'archive' && newLeadDetail.archiveLead?.callReminder && newLeadDetail.archiveLead?.customReminder === 'no-reminder') {
                    dispatch(updateError({ message: 'Archive scheduled — lead will be removed shortly', success: true }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'archive' && newLeadDetail.archiveLead?.callReminder) {
                    const addReminderObj = {
                        ...newLeadDetail.archiveLead?.addReminderObj,
                        lead: tableRowLead.lead_id || followLead.id,
                        lead_treatment: getCurrentTreatmentIdV2(tableRowLead?.treatment_id || followLead?.lead_treatments[0]?.id)
                    };
                    dispatch(addReminder(addReminderObj, practiceId, handleClose));
                    return;
                }
                if (status === 'leadNote') {
                    if (handleClose) {
                        handleClose('finish', data?.lead);
                    }
                    dispatch(leadActions.addLeadNotes(response?.data?.results || response?.data));;
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'leadNotePopup') {
                    if (handleClose) {
                        handleClose('finish', data?.lead);
                    }
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'callScript') {
                    // Silent save for call script auto-save - pass response data to callback
                    if (handleClose) {
                        handleClose({ success: true, status: 201, data: response.data, id: response.data?.id });
                    }
                    return;
                }
                if (status === 'nhs') {
                    dispatch(updateError({ message: 'Add to the NHS waiting list successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return
                }
                // Safety check: only proceed with archive flow if archiveLead exists
                if (archiveLead?.openCommunication) {
                    if (status === 'archive') {
                        dispatch(updateError({ message: 'Archive scheduled — lead will be removed shortly', success: true }));
                    }
                    const archivedData = { ...archiveLead.openCommunication, id: (archiveLead.openCommunication.id ? archiveLead.openCommunication.id : createLead.id) };
                    dispatch(updatePersonalDetail(archivedData, practiceId, handleClose, 'archived'));
                }
                return;
            }
            dispatch(updateCompleteStatus(false));
            const errorStr = JSON.stringify(response.data || 'Something went wrong');
            if (errorStr.toLowerCase().includes('object does not exist')) {
                dispatch(leadActions.setLeadDetailNotFound(true));
            }
            dispatch(updateError({ message: errorStr, success: false }));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            const errorStr = JSON.stringify(error.message || 'Something went wrong');
            if (errorStr.toLowerCase().includes('object does not exist')) {
                dispatch(leadActions.setLeadDetailNotFound(true));
            }
            dispatch(updateError({ message: errorStr, success: false }));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        }
    };
}

export function updateNotes(data, practiceId, noteId, handleCallback, status) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/notes/${noteId}/`, 'patch', data);

            if (response?.status === 200) {
                if (status === 'callScript') {
                    // Silent update for call script auto-save
                    if (handleCallback) {
                        handleCallback({ success: true, data: response.data });
                    }
                    return;
                }
                dispatch(updateError({ message: 'Note updated successfully', success: true }));
                if (handleCallback) {
                    handleCallback({ success: true, data: response.data });
                }
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                if (handleCallback) {
                    handleCallback({ success: false, error: response.data });
                }
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            if (handleCallback) {
                handleCallback({ success: false, error: error.message });
            }
        }
    };
}

export function getLeadNotes(id, practiceId, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/notes/?fields={additional_notes,contacted_via,id,lead,note,treatment,created_by,created_at,updated_at}&ordering=-updated_at`);
            if (response?.status === 200) {
                dispatch(leadActions.leadNote(response.data.results || response.data));;
            } else {
                errorHandler(response.data, handleNext, leadActions.hasError);
            }
        } catch (error) {
            errorHandler(error, handleNext, leadActions.hasError);
        }
    };
}

export function getLeadNotesWithFilter(id, practiceId, handleNext) {
    return async () => {
        try {
            const notes = ['AWAITING_RESPONSE', 'CHANGED_MIND', 'CREDIT_CHECK_FAILED', 'FOLLOW_UP', 'OTHER', 'TOO_EXPENSIVE'];
            const notesQueryParam = encodeURIComponent(notes.join(','));
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/notes/?fields={}&note__in=${notesQueryParam}`);
            if (response?.status === 200) {
                dispatch(leadActions.leadNoteWithFilter(response.data));;
            } else {
                errorHandler(response.data, handleNext, leadActions.hasError);
            }
        } catch (error) {
            errorHandler(error, handleNext, leadActions.hasError);
        }
    };
}

// ── Search ───────────────────────────────────────────────────

export function searchLead(data, practiceId, type, status, treatmentId) {
    return async () => {
        try {
            dispatch(leadActions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/search/?query=${data}${type === 'callScriptPreview' ? '&lead_treatments__status=NEW&is_archived=false' : type === 'FinancialTracker' ? `&is_archived=false&lead_treatments__isnull=false${status ? `&lead_treatments__status__in=${status}` : ''}` : type === 'taskLead' ? '&is_archived=false' : type === 'AlignerApprove' ? `&is_archived=false&lead_treatments__isnull=false&lead_treatments__status=TREATMENT_STARTED&lead_treatments__treatment__in=${treatmentId}&lead_treatment_attributes__contains=${JSON.stringify([{ value: 'Arrived' }])}` : ''}${type === 'ReviewLink' ? '&is_archived=false&lead_treatments__isnull=false' : ''}&${leadTableFields}`);
            if (response?.status === 200) {
                switch (type) {
                    case 'AlignerApprove':
                    case 'ReviewLink':
                    case 'FinancialTracker':
                    case 'taskLead':
                    case 'callScriptPreview':
                        dispatch(leadActions.addTaskSearchLead(response.data.results));
                        break;
                    default:
                        dispatch(leadActions.searchAPIdata(response.data.results));
                        break;
                }
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data || 'Something went wrong'), success: false }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };
}

// Universal search across all entity types (leads, treatments, appointments, invoices, conversations, notes, staff)
// Falls back to existing lead search if universal endpoint is not yet deployed
export function universalSearch(query, practiceId, category = null) {
    return async () => {
        try {
            dispatch(leadActions.startLoading());
            const categoryParam = category ? `&category=${category}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/search/?query=${encodeURIComponent(query)}${categoryParam}`);
            if (response?.status === 200) {
                dispatch(leadActions.setUniversalSearchResults(response.data));
            } else if (response?.status === 404) {
                // Universal endpoint not deployed yet — fall back to lead search
                const fallback = await callApi(`/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(query)}&${leadTableFields}`);
                if (fallback?.status === 200) {
                    dispatch(leadActions.searchAPIdata(fallback.data.results));
                    dispatch(leadActions.setUniversalSearchResults(null));
                }
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data || 'Something went wrong'), success: false }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };
}

export function searchCampaigns(query, practiceId) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/campaigns/?search=${encodeURIComponent(query)}&ordering=-created_at&limit=5`
            );
            if (response.ok && response.data?.results) {
                dispatch(leadActions.mergeCampaignSearchResults(response.data.results));
            }
        } catch (e) {
            // silent fail — campaign search is supplementary
        }
    };
}

// ── Archive ──────────────────────────────────────────────────

export function archivedLeadAPI(data, practiceId, handleClose, id) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/archive/`, 'post', data);
            if (response?.status === 200) {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: 'Lead will archived in 5 minutes.', success: true }));
                handleClose('finish', true);
                dispatch(startLoadingData(false));
                return;
            }
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data) || 'Something went wrong', success: false }));
            dispatch(startLoadingData(false));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(startLoadingData(false));
        }
    };
}

export function archivedLead({ practiceId, offset, limit, treatment, searchQuery, tags, referral, startDate, endDate, order, orderId, archiveReason, noTreatment }) {
    return async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const archiveSearchQuery = searchQuery ? `&search_query=${searchQuery}` : '';
            const archivedTags = tags ? `&tagged_items__tag__name__in=${tags}` : '';
            const archiveReferral = referral ? `&lead_source__referral__in=${referral}` : '';
            const archiveDate = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const reason = archiveReason ? `&archived_reason__in=${archiveReason}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/archived/?limit=${limit}&offset=${offset}${orderingLead}${treatmentQuery}${archiveSearchQuery}${archivedTags}${archiveReferral}${archiveDate}&is_archived=true${reason}`);
            if (response?.status === 200) {
                dispatch(archiveLead(response.data));
                dispatch(startLoadingData(false));
            } else {
                dispatch(leadActions.hasError(response.data.message || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.data.message));
        }
    };
}

export function restoreArchived(handleClose) {
    return async () => {
        try {
            const { newLeadDetail: { archiveLead }, tableRowLead } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${tableRowLead.lead_id}/restore/`, 'post', archiveLead.archiveData);
            if (response?.status === 200) {
                dispatch(updateError({ message: 'Lead will restore in 5 minutes.', success: true }));
                dispatch(updateCompleteStatus(false));
                handleClose('finish');
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
        }
    };
}

export function ArchivedLeadDashBoard(handleClose, followLead) {
    return async () => {
        try {
            const { newLeadDetail, tableRowLead } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            const treatmentId =
                tableRowLead?.treatment_id ||
                tableRowLead?.lead_treatments?.[0]?.id ||
                followLead?.lead_treatments?.[0]?.id ||
                '';
            const treatment = treatmentId !== undefined ? { treatment: treatmentId } : {};
            const archiveNote = {
                ...newLeadDetail.archiveLead.notes,
                lead: tableRowLead?.lead_id || followLead?.id,
                ...treatment,
            };
            dispatch(addNotes(archiveNote, practiceId, handleClose, 'archive', followLead));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

// ── Assign ───────────────────────────────────────────────────

export function UpdateAssignMember(data, practiceId, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/appointments/${data.id}/`, 'patch', data);
            if (response?.status === 200) {
                dispatch(getAppointment(practiceId, data?.lead));
                handleSuccess({ success: true, message: 'Appointment practitioner updated successfully' });
            }
        } catch (error) {
            const message = error?.message || 'An error occurred while updating the practitioner';
            handleSuccess({ message, success: false });
            dispatch(leadActions.hasError(message));
        }
    };
}

// ── Bulk operations ──────────────────────────────────────────

export function bulkArchivedOrRestore(practiceId, data, handleSuccess) {
    return async () => {
        try {
            if (data?.object_ids) {
                data.object_ids = data.object_ids.map(Number).filter(id => Number.isInteger(id) && id > 0);
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/bulk_archive_restore/`, 'post', data);
            if (response?.status === 200) {
                handleSuccess('finish');
                dispatch(updateError({ message: data.request_type === 'ARCHIVE' ? 'Lead will archived in 5 minutes.' : 'Restore completed successfully.', success: true }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
                handleSuccess('finish');
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function bulkUpdate(practiceId, data) {
    return async () => {
        try {
            if (data?.object_ids) {
                data.object_ids = data.object_ids.map(Number).filter(id => Number.isInteger(id) && id > 0);
            }
            if (data?.ids) {
                data.ids = data.ids.map(Number).filter(id => Number.isInteger(id) && id > 0);
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/bulk_update/?is_archived_included=true`, 'post', data);
            if (response?.status === 200) {
                dispatch(updateCompleteStatus(false));
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

// ── Misc ─────────────────────────────────────────────────────

export function getMergeTags() {
    return async () => {
        try {
            const response = await callApi('/api/v1/utils/mergetags/');
            if (response?.status === 200) {
                dispatch(leadActions.setMergeTags(response?.data));
                localStorage.setItem('MergeTagsData', JSON.stringify(response?.data));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function UnsubscribeLead(practiceId, data, callback) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/unsubscribe_email/`, 'post', data);
            if (response?.status === 200) {
                callback({ success: true, message: 'Lead unsubscribed successfully' });
            } else {
                callback({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' });
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };
}

export function ViewLeadsForExistingAudience(practiceId, fieldParams, filter, search) {
    return async () => {
        try {
            const limit = fieldParams?.limit ? `&limit=${fieldParams?.limit}` : '&limit=10';
            const offset = (fieldParams?.offset === 0 || fieldParams?.offset) ? `&offset=${fieldParams?.offset}` : '&offset=0';
            const param = `${limit}${offset}`;
            const additionalFilter = filter
                ? Object.entries(filter)
                    .map(([key, value]) => {
                        if (key === 'created_at__range') {
                            const [startDate, endDate] = value;
                            return `created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`;
                        }
                        return `${key}=${value}`;
                    })
                    .join('&')
                : '';

            const baseApi = search ? 'leads/search' : 'leads'

            const response = await callApi(`/api/v1/practices/${practiceId}/${baseApi}/?fields={created_at,created_at_human,dob,first_name,id,last_name,lead_contact,salutation,is_unsubscribed,contacted_count,updated_at,updated_at_human,is_archive_pending,is_archived,tags}&ordering=-created_at${param}&is_archived=false${additionalFilter ? `&${additionalFilter}` : ''}${search ? `&query=${search}` : ''}`);
            if (response.status === 200) {
                dispatch(leadActions.updateExistingAudienceList(response.data));
                dispatch(startLoadingData(false));

            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));

            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
            dispatch(startLoadingData(false));
        }
    };
}

export function archiveFromCurrentPracticeAndMove(currentPracticeId, nextPracticeId, nextPracticeName, currentPracticeName, leadDetails, handleSuccess) {
    return async () => {
        try {
            dispatch(updateError({ message: 'Moving Lead to New Practice', success: "warning" }));
            const dataPayload = {
                "salutation": leadDetails?.originalData?.salutation,
                "first_name": leadDetails?.originalData?.first_name,
                "dob": leadDetails?.originalData?.dob,
                "is_registered_patient": leadDetails?.originalData?.is_registered_patient,
                "lead_contact": {
                    "phone": leadDetails?.originalData?.lead_contact?.phone,
                    "email": leadDetails?.originalData?.lead_contact?.email,
                    "preferred_contact_method": leadDetails?.originalData?.lead_contact?.preferred_contact_method
                },
                "lead_source": {
                    "type": leadDetails?.originalData?.lead_source?.type,
                    "referral": leadDetails?.originalData?.lead_source?.referral
                },
                "last_name": leadDetails?.originalData?.last_name,
                "lead_address": {
                    "address_line_1": leadDetails?.originalData?.lead_address?.address_line_1,
                    "postcode": leadDetails?.originalData?.lead_address?.postcode,
                    "address_line_2": leadDetails?.originalData?.lead_address?.address_line_2,
                    "city": leadDetails?.originalData?.lead_address?.city,
                    "county": leadDetails?.originalData?.lead_address?.county
                },
                "assignees": [],
            }
            // NOTE: do NOT merge ad attribution here — this is the
            // practice-switcher path that moves an existing lead to a
            // different practice, not a fresh capture. Carrying the
            // current session's gclid would wrongly attribute a
            // lead-move as a new ad conversion. The original lead's
            // lead_source is copied via dataPayload already.
            let response = await callApi(`/api/v1/practices/${nextPracticeId}/leads/`, 'post', dataPayload);
            if (response?.status === 201) {
                await new Promise(resolve => setTimeout(resolve, 500));
                dispatch(updateError({ message: `Lead successfully moved to practice ${nextPracticeName}`, success: true }));
                const newLeadId = response?.data?.id;
                dispatch(updateError({ message: 'Adding notes to the lead details', success: "warning" }));
                response = await callApi(`/api/v1/practices/${nextPracticeId}/leads/${newLeadId}/notes/`, 'post', {
                    lead: newLeadId,
                    additional_notes: `This lead was moved to ${nextPracticeName} from ${currentPracticeName}`
                });
                if (response?.status === 201) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                    dispatch(updateError({ message: `Notes added successfully to Lead`, success: true }));
                } else {
                    throw new Error("Error in adding notes to the lead");
                }
                dispatch(updateError({ message: `Notes added successfully to Lead`, success: true }));
                await new Promise(resolve => setTimeout(resolve, 500));
                dispatch(updateError({ message: `Adding reminder in ${nextPracticeName} for the lead`, success: "warning" }));
                const remPayload = {
                    method: "CALL",
                    datetime: `${moment().add(2, 'hours').format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                    note: `This lead was moved to ${nextPracticeName} from ${currentPracticeName}`,
                    assignees: [],
                    is_completed: false,
                    group: 'TODO',
                    lead: newLeadId
                }
                response = await callApi(`/api/v1/practices/${nextPracticeId}/reminders/`, 'post', remPayload);
                if (response?.status === 201) {
                    dispatch(updateError({ message: `Reminder added successfully to Lead`, success: true }));
                } else {
                    throw new Error("Error in adding reminder to the lead");
                }
            } else {
                throw new Error("Failed to move lead");
            }
            const payload = {
                object_ids: [leadDetails?.originalData?.id],
                request_type: 'ARCHIVE',
                reason: `ANOTHER_PRACTICE`
            };
            response = await callApi(`/api/v1/practices/${currentPracticeId}/leads/bulk_archive_restore/`, 'post', payload);
            if (response?.status === 200) {
                dispatch(updateError({ message: 'Lead will archived in 5 minutes in current practice', success: true }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            console.error("Error caught", error);
            dispatch(updateError({ message: error?.message, success: false }));
        } finally {
            handleSuccess("finish")
        }
    }
}

export function conversations(id, practiceId) {
    // eslint-disable-next-line consistent-return
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/conversations/`);
            if (response?.status === 200) {
                dispatch(leadActions.chatbotConversationSlice(response.data));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error?.message));
        }
    };
}

export function getLeadSummary(practiceId, startDate, endDate, status) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/lead-treatment-category-summary/${status ? `?status=${status}` : ''}${startDate ? `?time_after=${startDate}&` : ''}${endDate ? `time_before=${endDate}` : ''}`);
            if (response?.status === 200) {
                dispatch(leadActions.leadSummaryListingSlice(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getLeadStatusSummary(practiceId, startDate, endDate, type) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/lead-status-summary/${startDate ? `?time_after=${startDate}&` : ''}${endDate ? `time_before=${endDate}` : ''}`);
            if (response?.status === 200) {
                if (type === 'ReportSummary') {
                    dispatch(leadActions.leadStatusSummaryListingSlice(response.data));
                }
                dispatch(leadActions.addDashboardStatusesSummery(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getAutoMatedMessages(practiceId, leadId, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/tasks/?fields={created_at,created_at_human,due,due_human,id,is_cancelled,is_enqueued,name,task_status,updated_at,updated_at_human,instance_object}`);
            if (response?.status === 200) {
                dispatch(leadActions.addAutoMatedMessages(response.data));
            } else {
                errorHandler(response.data, handleNext, leadActions.hasError);
            }
        } catch (error) {
            errorHandler(error, handleNext, leadActions.hasError);
        }
    };
}

export function AutoMatedMessageStatusUpdate(practiceId, leadId, id, detail, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/tasks/${id}/`, 'patch', detail);
            if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Message detail updated successfully' });
                dispatch(getAutoMatedMessages(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getLeadArchivedSummary(practiceId, startDate, endDate) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/archived-lead-summary/${startDate ? `?time_after=${startDate}&` : ''}${endDate ? `time_before=${endDate}` : ''}`);
            if (response?.status === 200) {
                dispatch(leadActions.leadArchivedSummaryListingSlice(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getBookingSuggestions({ practiceId, treatmentId }) {
    return async () => {
        try {
            const params = treatmentId ? `?treatment_id=${treatmentId}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/booking_suggestions/${params}`, 'get');
            if (response?.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch booking suggestions:', error);
            return null;
        }
    };
}
